/**
 * Which tenant a request to f-edge is for.
 *
 * A deployment for one client (tuuleliinid, kihnu, sark) sets `TENANT` in its pod env, and every f-edge call carries
 * `X-Bora-Tenant: <TENANT>`, as it always has.
 *
 * The Try Now storefront serves all trial tenants from one deployment, at `sales-<x>.try.bora.systems`. It sets no
 * `TENANT`: each f-edge call carries the standard `Host: <public host of the incoming request>` instead, and f-edge
 * (`request.getServerName()`) finds the trial tenant whose server has that host (f-edge accepts only TRIAL tenants by
 * host). This works because the call goes from this server straight to the in-cluster `http://f-edge.shared`, with no
 * ingress in between, and Node sends a custom `Host` unchanged. No `X-Forwarded-Host` goes out: f-edge trusts it from
 * cluster addresses, and it would replace the `Host`. The tenant is never read from a query, body or cookie.
 *
 * No Next.js imports here: `@/configs/axios` is in the client bundle too.
 */

export const BORA_TENANT_HEADER = 'X-Bora-Tenant'
/** The header that names the trial tenant: the standard `Host`, set to the public storefront host. */
export const HOST_HEADER = 'Host'

/** Never sent to f-edge: it trusts the header from cluster addresses, and it would replace `HOST_HEADER`. */
export const FORWARDED_HOST_HEADER = 'X-Forwarded-Host'

type Env = Record<string, string | undefined>

// A DNS host name: letters, digits, dots and hyphens. Anything else (spaces, a path, user info) is rejected.
const HOST_NAME_PATTERN = /^[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?$/

/** The tenant of a one-client deployment, or undefined in multi-tenant (trial) mode. */
export const getConfiguredTenant = (env: Env = process.env): string | undefined => env.TENANT?.trim() || undefined

/**
 * The host part of a `Host` or `X-Forwarded-Host` value: the first value of a list, lower case, with no port.
 * Returns undefined when the value is missing or is not a host name.
 */
export const normalizeHost = (raw: string | null | undefined): string | undefined => {
  const first = raw?.split(',')[0]?.trim().toLowerCase()

  if (!first) {
    return undefined
  }

  const host = first.replace(/:\d+$/, '')

  return HOST_NAME_PATTERN.test(host) ? host : undefined
}

type HeaderReader = { get(name: string): string | null }

/**
 * The storefront hosts a trial deployment serves. The default is the Try Now wildcard, `sales-<x>.try.bora.systems`
 * (and `.run`). `TRIAL_HOST_PATTERN` (a regular expression for the whole host) replaces it. f-edge has its own copy
 * (`app.trial.storefront-host-pattern`) and the two must stay equal. For local development use a hosts-file
 * name such as `sales-dev.try.bora.local`, never `localhost`: on f-edge that would send every local call through the
 * trial path.
 */
export const DEFAULT_TRIAL_HOST_PATTERN = /^sales-[a-z0-9-]+\.try\.bora\.(run|systems)$/

const getTrialHostPattern = (env: Env): RegExp => {
  const custom = env.TRIAL_HOST_PATTERN?.trim()

  return custom ? new RegExp(custom) : DEFAULT_TRIAL_HOST_PATTERN
}

/**
 * The public host of the incoming request, when it is a storefront host this deployment serves; else undefined.
 *
 * `Host` first: ingress-nginx sets it from the host the client asked for, while production passes a client's own
 * `X-Forwarded-Host` through (`use-forwarded-headers=true`). `X-Forwarded-Host` is read only when there is no `Host`.
 */
export const getRequestHost = (requestHeaders: HeaderReader, env: Env = process.env): string | undefined => {
  const host = normalizeHost(requestHeaders.get('host')) ?? normalizeHost(requestHeaders.get('x-forwarded-host'))

  return host && getTrialHostPattern(env).test(host) ? host : undefined
}

/**
 * The headers that name the tenant of an f-edge call, for a tenant key from `getRequestTenantKey`.
 *
 * With `TENANT` set the axios instance already sends `X-Bora-Tenant`, so this adds nothing. Without it, the key is
 * the request host and goes out as `Host`.
 */
export const getTenantHeaders = (tenantKey: string, env: Env = process.env): Record<string, string> =>
  getConfiguredTenant(env) ? {} : { [HOST_HEADER]: tenantKey }

export class MissingTenantError extends Error {
  constructor() {
    super('No TENANT is set and the request has no storefront host: refusing to call f-edge without a tenant')
    this.name = 'MissingTenantError'
  }
}

/**
 * The tenant of the current request: `TENANT` when it is set, otherwise the public host of the request (a trial
 * storefront host, see `getRequestHost`).
 *
 * Call it in request scope, never inside `unstable_cache` (Next.js does not allow `headers()` there). A cached
 * function takes the key as an argument instead, so the key is part of its cache key: two trial tenants never
 * share a cached response.
 *
 * Throws `MissingTenantError` when there is neither: an f-edge call without a tenant must not go out.
 */
export const getRequestTenantKey = async (env: Env = process.env): Promise<string> => {
  const tenant = getConfiguredTenant(env)

  if (tenant) {
    return tenant
  }

  // A dynamic import: a static one would put next/headers into the client bundle, which Next.js rejects
  const { headers } = await import('next/headers')
  const host = getRequestHost(await headers(), env)

  if (!host) {
    throw new MissingTenantError()
  }

  return host
}
